import { Card, CardBody, Stacked } from '@statsbomb/kitbag-components';

import { FilterScopeProvider, ResetAllFiltersButton } from '@statsbomb/kitbag-global-football';
import { MetricValueMode } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { ScopeProvider } from 'jotai-scope';
import { ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import {
  playerGameDataTableIdAtom,
  unwrappedPlayerGameDataDefaultColumnsAtom,
} from '@/atoms/dataLocker/player/game/table';
import { unwrappedSelectedPlayerNameAtom } from '@/atoms/player/info';
import { playerIdAtom } from '@/atoms/player/player';
import { playerGameStatsFilterParamsAtom } from '@/atoms/queries/player/playerAggData';
import { playerGameAggsCountAtom } from '@/atoms/queries/player/playerGameAggData';
import { tableColumnEntitiesAtom } from '@/atoms/queries/tableColumns';
import { metricValueModeAtom } from '@/atoms/table';
import { TableDataControls } from '@/components/controls/TableDataControls';
import { PageTitle } from '@/components/head/PageTitle';
import { LayoutWithFilters } from '@/components/layout/LayoutWithFilters';
import { PlayerHighLevelFilters } from '@/components/player/filters/PlayerHighLevelFilters';
import { PlayerGameAggsTable } from '@/components/player/tables/PlayerGameAggsTable';
import { ScopedTableWithDefaultColumns } from '@/components/tables/ScopedTableWithDefaultColumns';
import { TablePagination } from '@/components/tables/TablePagination';
import { useCsvDownload } from '@/hooks/exporting/useCsvDownload';
import { useEntityPageTitle } from '@/hooks/useEntityPageTitle';
import { useTableColumns } from '@/hooks/useTableColumns';
import { playerGamesTotalAggregatesUrl } from '@/query/url';

const CsvDownload = ({ pageTitle }: { pageTitle: string }) => {
  const playerId = useAtomValue(playerIdAtom);
  const playerGameStatsFilterParams = useAtomValue(playerGameStatsFilterParamsAtom);
  useCsvDownload(pageTitle, playerGamesTotalAggregatesUrl(playerId, playerGameStatsFilterParams));
  return null;
};

const HydratePlayerGamesAtoms = ({ children }: { children: ReactNode }) => {
  useHydrateAtoms([[metricValueModeAtom, MetricValueMode.Total]]);
  useTableColumns(['competition', 'game', 'player_total_metric']);
  return children;
};

export const PlayerGamesPage = () => {
  const { t } = useTranslation(['navigation', 'games', 'general']);
  const playerName = useAtomValue(unwrappedSelectedPlayerNameAtom);
  const pageTitle = useEntityPageTitle(t('secondary.games', { ns: 'navigation' }), playerName);

  const tableId = useAtomValue(playerGameDataTableIdAtom);
  return (
    <ScopeProvider atoms={[tableColumnEntitiesAtom, metricValueModeAtom]}>
      <HydratePlayerGamesAtoms>
        <PageTitle>{pageTitle}</PageTitle>
        <FilterScopeProvider scope="main">
          <LayoutWithFilters
            filters={<PlayerHighLevelFilters />}
            filtersTitle={t('filters.other', { ns: 'general' })}
            filtersActions={<ResetAllFiltersButton />}
          >
            <Suspense>
              <Card>
                <ScopedTableWithDefaultColumns
                  defaultColumnsAtom={unwrappedPlayerGameDataDefaultColumnsAtom}
                  tableId={tableId}
                >
                  <CsvDownload pageTitle={pageTitle} />
                  <TableDataControls heading={t('matchStats', { ns: 'games' })} />
                  <CardBody>
                    <Stacked direction="column" gap="medium">
                      <PlayerGameAggsTable />
                      <TablePagination dataLengthAtom={playerGameAggsCountAtom} />
                    </Stacked>
                  </CardBody>
                </ScopedTableWithDefaultColumns>
              </Card>
            </Suspense>
          </LayoutWithFilters>
        </FilterScopeProvider>
      </HydratePlayerGamesAtoms>
    </ScopeProvider>
  );
};
